import { motion } from 'motion/react';

import type { AnalysisOutput } from '@smartapply/shared';

interface DiagnosisSummaryProps {
  output: AnalysisOutput;
}

/**
 * Síntese visual no topo do diagnóstico — antes da listagem detalhada.
 *
 * Composta por:
 *   - Numerais display em serif italic (forças / críticos / sugestões / quick wins)
 *   - Régua horizontal proporcional mostrando distribuição de severities
 *
 * NÃO é gauge. NÃO é card grid 2×2. É composição editorial com peso visual
 * vindo da tipografia display + cor semântica.
 */
export function DiagnosisSummary({ output }: DiagnosisSummaryProps) {
  const critical = output.findings.filter((f) => f.severity === 'critical').length;
  const suggestions = output.findings.filter((f) => f.severity === 'suggestion').length;
  const strengths = output.strengths.length;
  const quickWinsMin = output.quickWins.reduce((s, q) => s + q.estimatedTimeMinutes, 0);

  const total = critical + suggestions + strengths;
  const segs =
    total === 0
      ? []
      : [
          { kind: 'critical' as const, count: critical, color: 'var(--accent-crimson)' },
          { kind: 'suggestion' as const, count: suggestions, color: 'var(--accent-saffron)' },
          { kind: 'positive' as const, count: strengths, color: 'var(--accent-forest)' },
        ].filter((s) => s.count > 0);

  return (
    <motion.section
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: 0.1 }}
      className="card-paper relative overflow-hidden rounded-sm px-7 py-6"
    >
      <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-[var(--ink-tertiary)]">
        síntese
      </p>

      {/* Numerais grandes em italic serif — cada um com sua cor semântica */}
      <div className="mt-3 grid grid-cols-2 gap-x-8 gap-y-3 sm:grid-cols-4">
        <Stat
          value={strengths}
          label="forças"
          color="var(--accent-forest)"
        />
        <Stat
          value={critical}
          label="críticos"
          color="var(--accent-crimson)"
        />
        <Stat
          value={suggestions}
          label="sugestões"
          color="var(--accent-saffron)"
        />
        <Stat
          value={`~${quickWinsMin}`}
          label="min de fixes"
          color="var(--ink-secondary)"
          unit="min"
        />
      </div>

      {/* Régua horizontal proporcional — distribuição de severities */}
      {total > 0 && (
        <div className="mt-6">
          <div className="flex h-2 w-full overflow-hidden rounded-sm bg-[var(--bg-paper-recessed)]">
            {segs.map((s, i) => (
              <motion.div
                key={s.kind}
                initial={{ width: 0 }}
                animate={{ width: `${(s.count / total) * 100}%` }}
                transition={{ duration: 0.6, delay: 0.3 + i * 0.1, ease: 'easeOut' }}
                style={{ backgroundColor: s.color }}
                title={`${s.kind}: ${s.count}`}
              />
            ))}
          </div>
          <dl className="mt-2 flex flex-wrap items-center gap-x-5 gap-y-1 font-sans text-[11px] text-[var(--ink-tertiary)]">
            {segs.map((s) => (
              <div key={s.kind} className="flex items-center gap-1.5">
                <span
                  aria-hidden
                  className="inline-block h-2 w-2 rounded-full"
                  style={{ backgroundColor: s.color }}
                />
                <dt className="capitalize">{labelOfKind(s.kind)}</dt>
                <dd className="font-mono tabular-nums text-[var(--ink-secondary)]">{s.count}</dd>
              </div>
            ))}
          </dl>
        </div>
      )}
    </motion.section>
  );
}

function Stat({
  value,
  label,
  color,
  unit,
}: {
  value: number | string;
  label: string;
  color: string;
  unit?: string;
}) {
  return (
    <div>
      <p className="flex items-baseline gap-1.5">
        <span
          className="font-serif text-[44px] font-medium italic leading-none tabular-nums"
          style={{ color }}
        >
          {value}
        </span>
        {unit && (
          <span className="font-mono text-[11px] text-[var(--ink-tertiary)]">{unit}</span>
        )}
      </p>
      <p className="mt-1 font-sans text-xs text-[var(--ink-tertiary)]">{label}</p>
    </div>
  );
}

function labelOfKind(k: 'critical' | 'suggestion' | 'positive'): string {
  return ({ critical: 'críticos', suggestion: 'sugestões', positive: 'forças' } as const)[k];
}
